import apisauce from 'apisauce';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import fileDownload from 'js-file-download';

import { newCancelToken, stringify } from 'src/utils';
import appConfig from 'src/appConfig';
import { SignInPayload, ExchangeTokenPayLoad, ChangePasswordPayload } from 'src/redux/authRedux/types';
import { TokenService } from '.';
import { CreateUserPayload, GetUserDetailParam, GetUsersParams, UpdateUserPayload } from 'src/redux/usersRedux/types';
import { GetReportsParams } from 'src/redux/reportRedux/types';
import { GetQuarantinesParams } from 'src/redux/quarantineRedux/types';
import { GetTestingsParams } from 'src/redux/testRedux/types';
import {
  GetDepositionsParam,
  DeleteParam,
  RenameFileParam,
  CreateDepositionPayload,
  ApproveDepositionPayload,
  LockDepositionParam,
  GetDepositionDetailParam,
  RejectPayload,
  GrantAccessFinalPayload,
  GetDepositionFileParam,
} from 'src/redux/depositionRedux/types';
import { FilePreDownloadPayload, FilePreUploadPayload } from 'src/redux/fileRedux/types';
import { getFileType } from 'src/utils/fileUtils';
import { GetScheduleDepositionsParam, UpdateScheduleDepositionPayload } from 'src/redux/scheduleDepositionRedux/types';

const create = (baseURL = appConfig.API_URL) => {
  //
  // Create and configure an apisauce-based api object.
  //

  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 0,
      Accept: 'application/json',
    },
    timeout: appConfig.CONNECTION_TIMEOUT,
  });

  api.axiosInstance.interceptors.request.use(config => {
    return TokenService.getToken()
      .then(token => {
        config.headers.Authorization = 'Bearer ' + token;
        return Promise.resolve(config);
      })
      .catch(() => {
        return Promise.resolve(config);
      });
  });

  //REFRESH TOKEN
  api.axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      if (error.response.status === 500) {
        try {
          const originalRequest = error.config;
          if (originalRequest.url === '/sessions') {
            localStorage.clear();
            Auth.signOut();
            return Promise.reject(error);
          }
        } catch (err) {
          return Promise.reject(error);
        }
      }
      if (error.response.status === 401) {
        try {
          const originalRequest = error.config;
          if (originalRequest.url === '/sessions') {
            localStorage.clear();
            Auth.signOut();
            return Promise.reject(error);
          }

          await TokenService.clearToken();
          const idToken = await TokenService.getToken();
          const { data } = await exchangeToken({ idToken: idToken as string });
          localStorage.setItem('api-token', (data as any).token);
          api.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + (data as any).token;
          return api.axiosInstance(originalRequest);
        } catch (err) {
          return Promise.reject(error);
        }
      }

      return error;
    },
  );

  const getRoot = () => api.get('');

  // ====================== Auth ======================
  const signIn = (body: SignInPayload) => Auth.signIn(body.username, body.password);
  const exchangeToken = (body: ExchangeTokenPayLoad) => api.post('/sessions', body, newCancelToken());

  const signOut = () => Auth.signOut();
  const getPermission = () => api.get('/me/permission', {}, newCancelToken());
  const getCurrentProfile = () => api.get('/users/authenticated', {}, newCancelToken());

  const getUserById = (params: GetUserDetailParam) => api.get(`/users/${params.id}`, {}, newCancelToken());

  const createUser = (body: CreateUserPayload) => api.post('/users', body, newCancelToken());

  const updateUser = (data: UpdateUserPayload) => api.put(`/users/${data.id}`, data.body, newCancelToken());
  const deleteUser = (data: GetUserDetailParam) => api.delete(`/users/${data.id}`, {}, newCancelToken());
  const getUsers = (params: GetUsersParams) => {
    const queryString = stringify(params);

    return api.get(`/users?${queryString}`, {}, newCancelToken());
  };

  const getTenants = () => api.get('/tenant', {}, newCancelToken());

  const changePassword = (body: ChangePasswordPayload) =>
    Auth.changePassword(body.user, body.currentPassword, body.newPassword);

  // ====================== Content ======================
  const getUserRole = () => api.get('/roles', {}, newCancelToken());

  // ====================== Report ======================
  const getReports = (params: GetReportsParams) => {
    const queryString = stringify(params);

    return api.get(`/admin/report?${queryString}`, {}, newCancelToken());
  };

  const getUserHealthReportById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/daily-check-in`, {}, newCancelToken());
  const getUserLocationReportById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/location-check-in`, {}, newCancelToken());
  const getUserContactReportById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/contact-history`, {}, newCancelToken());

  // ====================== Quarantine ======================

  const getQuarantines = (params: GetQuarantinesParams) => {
    const queryString = stringify(params);

    return api.get(`/admin/report/quarantine?${queryString}`, {}, newCancelToken());
  };

  const getUserHealthQuarantineById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/daily-check-in`, {}, newCancelToken());
  const getUserLocationQuarantineById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/location-check-in`, {}, newCancelToken());
  const getUserContactQuarantineById = (params: GetUserDetailParam) =>
    api.get(`/admin/report/user/${params.id}/contact-history`, {}, newCancelToken());
  // ====================== Testing ======================

  const getTestRegistrations = (params: GetTestingsParams) => {
    const queryString = stringify(params);
    return api.get(`/admin/lab-registration?${queryString}&isTested=false`, {}, newCancelToken());
  };

  const getTestResults = (params: GetTestingsParams) => {
    const queryString = stringify(params);
    return api.get(`/admin/lab-registration?${queryString}&isTested=true`, {}, newCancelToken());
  };
  const getTestDetailInfo = (params: GetUserDetailParam) =>
    api.get(`/admin/lab-registration/${params.id}`, {}, newCancelToken());

  // ====================== Deposition ======================
  const createDeposition = (body: CreateDepositionPayload) => api.post('/depositions', body, newCancelToken());
  const updateDeposition = (payload: { body: CreateDepositionPayload; uri: string }) =>
    api.put(payload.uri, payload.body, newCancelToken());
  const deleteDeposition = (params: DeleteParam) => api.delete(params.uri, {}, newCancelToken());
  const approveDeposition = (params: ApproveDepositionPayload) =>
    api.patch(params.uri, { action: 'APPROVE', approveData: params.approveData }, newCancelToken());

  const rejectDeposition = (params: RejectPayload) =>
    api.patch(params.uri, { action: 'REJECT', rejectionReason: params.reason }, newCancelToken());

  const grantAccessFinalDeposition = (params: GrantAccessFinalPayload) =>
    api.patch(
      `${params.uri}`,
      { action: 'UPDATE_FINAL_DEPOSITION_ACCESS', updateAccessData: params.updateAccessData },
      newCancelToken(),
    );
  const completeDeposition = (params: GetDepositionDetailParam) =>
    api.patch(`${params.uri}`, { action: 'COMPLETE' }, newCancelToken());
  const lockDeposition = (params: LockDepositionParam) =>
    api.patch(params.uri, { action: params.action }, newCancelToken());

  const getDepositions = (params: GetDepositionsParam) => {
    return api.post(`/depositions/search-results`, params, newCancelToken());
  };

  const getDepositionById = (params: GetDepositionDetailParam) =>
    api.get(`/depositions/${params.uri}`, {}, newCancelToken());

  const getDepositionFiles = (data: GetDepositionFileParam) => {
    if (data.params) {
      const queryString = stringify(data.params);
      return api.get(`/depositions${data.uri}?${queryString}`, {}, newCancelToken());
    }
    return api.get(`/depositions${data.uri}`, {}, newCancelToken());
  };

  const getDepositionRequester = (params: GetDepositionDetailParam) => api.get(params.uri, {}, newCancelToken());

  const getDepositionInvoledParties = (params: GetDepositionDetailParam) => api.get(params.uri, {}, newCancelToken());

  const renameFile = (params: RenameFileParam) =>
    api.patch(params.uri, { action: 'RENAME', renameData: { newName: params.newName } }, newCancelToken());

  const deleteFile = (params: DeleteParam) => api.delete(params.uri, {}, newCancelToken());

  // ====================== File ======================
  const getExhibitUploadUrl = (body: FilePreUploadPayload) => {
    const { file } = body;
    return api.get(
      `/depositions/${body.depositionId}/exhibit-uploaders/upload-url?fileName=${file.name}&contentType=${getFileType(
        file,
      )}`,
      {},
      newCancelToken(),
    );
  };

  const getMultiPartExhibitUploadUrl = (body: FilePreUploadPayload) => {
    const { file } = body;
    return api.get(
      `/depositions/${body.depositionId}/exhibit-uploaders/multi-part-upload?fileName=${file.name}&contentType=${body.contentType}&partNumber=${body.partNumber}`,
      {},
      newCancelToken(),
    );
  };

  const preExhibitUpload = (body: FilePreUploadPayload) =>
    api.post(
      `/depositions/${body.depositionId}/exhibit-uploaders`,
      { fileName: body.file.name, contentType: body.contentType, size: body.file.size },
      newCancelToken(),
    );

  const getFinalUploadUrl = (body: FilePreUploadPayload) =>
    api.get(
      `/depositions/${body.depositionId}/final-deposition-files/upload-url?fileName=${
        body.file.name
      }&contentType=${getFileType(body.file)}`,
      {},
      newCancelToken(),
    );

  const getMultiPartFinalUploadUrl = (body: FilePreUploadPayload) => {
    const { file } = body;
    return api.get(
      `/depositions/${body.depositionId}/final-deposition-files/multi-part-upload?fileName=${file.name}&contentType=${body.contentType}&partNumber=${body.partNumber}`,
      {},
      newCancelToken(),
    );
  };

  const preFinalUpload = (body: FilePreUploadPayload) =>
    api.post(
      `/depositions/${body.depositionId}/final-deposition-files`,
      { fileName: body.file.name, contentType: getFileType(body.file), size: body.file.size },
      newCancelToken(),
    );
  const getProductionUploadUrl = (body: FilePreUploadPayload) =>
    api.get(
      `/depositions/${body.depositionId}/production-files/upload-url?fileName=${
        body.file.name
      }&contentType=${getFileType(body.file)}`,
      {},
      newCancelToken(),
    );
  const getMultiPartProductionUploadUrl = (body: FilePreUploadPayload) => {
    const { file } = body;
    return api.get(
      `/depositions/${body.depositionId}/production-files/multi-part-upload?fileName=${file.name}&contentType=${body.contentType}&partNumber=${body.partNumber}`,
      {},
      newCancelToken(),
    );
  };
  const preProductionUpload = (body: FilePreUploadPayload) =>
    api.post(
      `/depositions/${body.depositionId}/production-files`,
      { fileName: body.file.name, contentType: getFileType(body.file), size: body.file.size },
      newCancelToken(),
    );

  const prepareDownload = (params: FilePreDownloadPayload) => {
    return api.get(`${params.downloadUri}`, {}, newCancelToken()).then(async (response: any) => {
      if (response.status === 200) {
        if (response.data?.url) {
          await axios
            .get(response.data.url, {
              responseType: 'blob',
            })
            .then((fileResponse: any) => {
              fileDownload(fileResponse.data, params.fileName);
            });
        }
      }
    });
  };
  const uploadFile = (body: FilePreUploadPayload, uploadUrl: string, progressCallback: any) => {
    return axios.put(uploadUrl, body.file, {
      headers: {
        'Content-Type': body.contentType,
      },
      onUploadProgress: progress => {
        const { loaded, total } = progress;
        const percentageProgress = Math.floor((loaded / total) * 100);
        progressCallback({ id: body.id, progress: percentageProgress });
      },
      ...newCancelToken(120 * 60 * 1000), // 30 minutes
    });
  };

  // ====================== Schedule Deposition ======================
  const getScheduleDepositions = (params: GetScheduleDepositionsParam) => {
    const queryString = stringify(params);
    return api.get(`/schedule-depositions?${queryString}`, {}, newCancelToken());
  };

  const updateScheduleDeposition = (payload: { body: UpdateScheduleDepositionPayload; uri: string }) => {
    return api.put(payload.uri, payload.body, newCancelToken());
  };

  // ====================== File ======================
  const completeMultiPartUpload = (body: any) => {
    return api.post(`/file/complete-multi-part-upload`, body, newCancelToken());
  };

  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  //
  // Notice we're not returning back the `api` created in step 1. That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    getRoot,
    // ====================== Auth ======================
    getPermission,
    getCurrentProfile,
    signIn,
    signOut,
    exchangeToken,
    changePassword,

    // ====================== Users ======================
    getUsers,
    getUserById,
    createUser,
    deleteUser,
    // ====================== Reports ======================
    getReports,
    getUserHealthReportById,
    getUserLocationReportById,
    getUserContactReportById,
    updateUser,

    getTenants,

    // ====================== Quarantine ======================
    getQuarantines,
    getUserHealthQuarantineById,
    getUserLocationQuarantineById,
    getUserContactQuarantineById,

    // ====================== Testing ======================
    getTestRegistrations,
    getTestDetailInfo,
    getTestResults,
    // ====================== Content ======================
    getUserRole,

    // ====================== Deposition ======================
    createDeposition,
    updateDeposition,
    deleteDeposition,
    approveDeposition,
    rejectDeposition,
    grantAccessFinalDeposition,
    lockDeposition,
    completeDeposition,
    getDepositions,
    getDepositionById,
    getDepositionFiles,
    getDepositionRequester,
    getDepositionInvoledParties,
    renameFile,
    deleteFile,
    // ====================== File ===================
    getExhibitUploadUrl,
    getMultiPartExhibitUploadUrl,
    preExhibitUpload,
    getFinalUploadUrl,
    getMultiPartFinalUploadUrl,
    preFinalUpload,
    getProductionUploadUrl,
    getMultiPartProductionUploadUrl,
    preProductionUpload,
    prepareDownload,
    uploadFile,
    completeMultiPartUpload,

    // ====================== Schedule Deposition ======================
    getScheduleDepositions,
    updateScheduleDeposition,
  };
};

export default {
  create,
};
